import React, { FC, Fragment, useState, useEffect } from "react";
import { Popover, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";

export interface TabFiltersProps {
  onCountryChange: (country: string | null) => void;
  onUserChange: (user: string | null) => void;
  onTriChange: (tri: string | null) => void;
  onSearchChange?: (search: string) => void;
  onResetFilters?: () => void;
  tabs?: string[];
  tabsUsers?: string[];
  pays?: string;
}

const typeOfTris = [
  { name: "triPubCroi", translationKey: "triPubCroi" },
  { name: "triPubDecroi", translationKey: "triPubDecroi" },
  { name: "triCreaCroi", translationKey: "triCreaCroi" },
  { name: "triCreaDecroi", translationKey: "triCreaDecroi" },
  { name: "triNbJCroi", translationKey: "triNbJCroi" },
  { name: "triNbJDecroi", translationKey: "triNbJDecroi" },
  { name: "triNbLCroi", translationKey: "triNbLCroi" },
  { name: "triNbLDecroi", translationKey: "triNbLDecroi" },
];

const TabFilters: FC<TabFiltersProps> = ({
  onCountryChange = () => {},
  tabs = [],
  onUserChange = () => {},
  tabsUsers = [],
  onTriChange = () => {},
  onSearchChange = () => {},
  onResetFilters = () => {},
  pays,
}) => {
  const { t } = useTranslation();

  const [isCountrySelected, setIsCountrySelected] = useState(false);
  const [isUserSelected, setIsUserSelected] = useState(false);
  const [isTriSelected, setIsTriSelected] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const [selectedCountryLabel, setSelectedCountryLabel] = useState<string | null>(pays || null);
const [selectedUserLabel, setSelectedUserLabel] = useState<string | null>(null);
const [selectedTriLabel, setSelectedTriLabel] = useState<string | null>(null);

  useEffect(() => {
    if (pays && pays !== "") {
      setIsCountrySelected(true);
    }
  }, [pays]);

const dropdownPanelClass = `
  absolute
  left-0
  top-full
  z-[9999]
  mt-3
  w-[300px]
`;

const dropdownPanelCountryClass = `
  absolute
  top-full
  z-[9999]
  mt-3

  w-[300px]
  max-w-[calc(100vw-32px)]

left-2/3
-translate-x-1/2

  sm:left-0
  sm:translate-x-0
`;

const dropdownPanelUserClass = `
  absolute
  top-full
  z-[9999]
  mt-3

  w-[300px]
  max-w-[calc(100vw-32px)]

  right-0
  left-auto

  sm:left-0
  sm:right-auto
`;

const dropdownBoxClass = `
  overflow-hidden

  rounded-[28px]

  border
  border-neutral-200
  dark:border-neutral-700

  bg-white
  dark:bg-neutral-900

  shadow-[0_20px_60px_rgba(15,23,42,0.12)]
  ring-1
  ring-black/5
`;

const dropdownItemClass = (isSelected: boolean) => `
  group

  flex
  w-full
  items-center
  justify-between

  rounded-2xl

  px-4
  py-2

  text-left

  transition

  ${
    isSelected
      ? "bg-violet-50 text-violet-700 dark:bg-violet-950/30 dark:text-violet-300"
      : "text-neutral-800 hover:bg-violet-50/60 hover:text-violet-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
  }
`;

  const filterButtonClass = (active: boolean, open: boolean) => `
    inline-flex
    items-center
    justify-center
    gap-2

    px-3
py-2

text-xs
font-semibold

sm:px-5
sm:py-3
sm:text-sm

    rounded-full

    border
    shadow-sm

    transition-all
    duration-200

    focus:outline-none

    ${
      open || active
        ? "bg-violet-50 text-violet-700 border-violet-200"
        : "bg-white text-neutral-700 border-neutral-200 hover:border-violet-200 hover:text-violet-600"
    }

    dark:bg-neutral-900
    dark:border-neutral-700
    dark:text-neutral-200
  `;

  const renderXClearCountry = (close: () => void) => {
    return (
      <span
        className="w-4 h-4 rounded-full bg-violet-500 text-white flex items-center justify-center ml-1 cursor-pointer"
       onClick={(e) => {
  e.stopPropagation();

  onCountryChange(null);
  setIsCountrySelected(false);
  setSelectedCountryLabel(null);

  close();
}}
      >
        <i className="las la-times text-[10px]" />
      </span>
    );
  };

  const renderXClearUser = (close: () => void) => {
    return (
      <span
        className="w-4 h-4 rounded-full bg-violet-500 text-white flex items-center justify-center ml-1 cursor-pointer"
       onClick={(e) => {
  e.stopPropagation();

  onUserChange(null);
  setIsUserSelected(false);
  setSelectedUserLabel(null);

  close();
}}
      >
        <i className="las la-times text-[10px]" />
      </span>
    );
  };

const renderTabsTris = () => {
  return (
    <Popover className="relative z-[9999]">
      {({ open, close }) => (
        <>
          <Popover.Button className={filterButtonClass(isTriSelected, open)}>
            <span  className="text-xs md:text-md">{t("tri")}</span>
            <i className="las la-angle-down text-base" />
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1 scale-95"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-1 scale-95"
          >
            <Popover.Panel className="absolute left-0 top-full z-[9999] mt-3 w-[340px] -translate-x-9">
              <div className={dropdownBoxClass}>
                <div className="border-b border-neutral-100 px-5 py-4 dark:border-neutral-800">
                  <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                    {t('trierVoyages')}
                  </p>
              
                </div>

                <div className="max-h-[360px] overflow-y-auto p-2">
                  {typeOfTris.map((tri) => {
                    const isSelected = selectedTriLabel === tri.name;

                    return (
                      <button
                        key={tri.name}
                        type="button"
                        className={dropdownItemClass(isSelected)}
                        onClick={() => {
                          onTriChange(tri.name);
                          setSelectedTriLabel(tri.name);
                          setIsTriSelected(true);
                          close();
                        }}
                      >
                        <span className="text-[15px] font-medium leading-snug">
                          {t(tri.translationKey)}
                        </span>

                        {isSelected && (
                          <span className="ml-3 flex h-6 w-6 items-center justify-center rounded-full bg-violet-100 text-violet-600 dark:bg-violet-900/50 dark:text-violet-300">
                            <i className="las la-check text-sm" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

  const renderCountryDropdown = () => {
  return (
    <Popover className="relative z-[9999]">
      {({ open, close }) => (
        <>
          <Popover.Button className={filterButtonClass(isCountrySelected, open)}>
            <span className="text-xs md:text-md">{t("pays")}</span>
            <i className="las la-angle-down text-base" />
            {isCountrySelected && renderXClearCountry(close)}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1 scale-95"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-1 scale-95"
          >
            <Popover.Panel className={dropdownPanelCountryClass}>
              <div className={dropdownBoxClass}>
                <div className="border-b border-neutral-100 px-5 py-4 dark:border-neutral-800">
                  <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                    {t('filtrerPays')}
                  </p>
   
                </div>

                <div className="max-h-[360px] overflow-y-auto p-2">
                  {tabs.map((country) => {
                    const isSelected = selectedCountryLabel === country;

                    return (
                      <button
                        key={country}
                        type="button"
                        className={dropdownItemClass(isSelected)}
                        onClick={() => {
                          const nextCountry = country === "all" ? null : country;

                          onCountryChange(nextCountry);
                          setSelectedCountryLabel(nextCountry);
                          setIsCountrySelected(nextCountry !== null);

                          close();
                        }}
                      >
                        <span className="text-[15px] md:text-[15px] font-medium leading-snug">
                          {country}
                        </span>

                        {isSelected && (
                          <span className="ml-3 flex h-6 w-6 items-center justify-center rounded-full bg-violet-100 text-violet-600 dark:bg-violet-900/50 dark:text-violet-300">
                            <i className="las la-check text-sm" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

 const renderTabsUsers = () => {
  return (
    <Popover className="relative z-[9999]">
      {({ open, close }) => (
        <>
          <Popover.Button className={filterButtonClass(isUserSelected, open)}>
            <span className="text-xs md:text-md">{t("utilisateur")}</span>
            <i className="las la-angle-down text-base" />
            {isUserSelected && renderXClearUser(close)}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1 scale-95"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-1 scale-95"
          >
            <Popover.Panel className={dropdownPanelUserClass}>
              <div className={dropdownBoxClass}>
                <div className="border-b border-neutral-100 px-5 py-4 dark:border-neutral-800">
                  <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                    {t('filtrerUtilisateur')}
                  </p>
    
                </div>

                <div className="max-h-[360px] overflow-y-auto p-2">
                  {tabsUsers.map((user) => {
                    const isSelected = selectedUserLabel === user;

                    return (
                      <button
                        key={user}
                        type="button"
                        className={dropdownItemClass(isSelected)}
                        onClick={() => {
                          const nextUser = user === "all" ? null : user;

                          onUserChange(nextUser);
                          setSelectedUserLabel(nextUser);
                          setIsUserSelected(nextUser !== null);

                          close();
                        }}
                      >
                        <span className="text-[15px] font-medium leading-snug">
                          {user}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

  return (
    <div
      className="
        w-full

        rounded-[28px]

        bg-white/85
        dark:bg-neutral-900/85

        border
        border-neutral-100
        dark:border-neutral-800

        shadow-sm
        backdrop-blur

        px-4
        py-4
      "
    >
      <div
        className="
          flex
          flex-col
          gap-3

          lg:flex-row
          lg:items-center
        "
      >
       <div
  className="
    flex
    items-center
    gap-1
    md:gap-3
    flex-shrink-0
    overflow-visible
  "
>
  {renderTabsTris()}
  {renderCountryDropdown()}
  {renderTabsUsers()}
</div>

        <div className="relative flex-1 min-w-0">
          <i
            className="
              las
              la-search

              absolute
              left-4
              top-1/2
              -translate-y-1/2

              text-lg
              text-neutral-400
            "
          />

          <input
            type="text"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              onSearchChange(e.target.value);
            }}
            placeholder={` ${t('filtreRecherche')}`}
            className="
              w-full

              rounded-full

              border
              border-neutral-200
              dark:border-neutral-700

              bg-white
              dark:bg-neutral-950

              py-3
              pl-11
              pr-4

              text-sm
              text-neutral-900
              dark:text-white

              placeholder:text-neutral-400

              shadow-sm

              focus:outline-none
              focus:ring-2
              focus:ring-violet-100
              focus:border-violet-200
            "
          />
        </div>

        <button
          type="button"
          onClick={() => {
  setSearchValue("");

  setIsCountrySelected(false);
  setIsUserSelected(false);
  setIsTriSelected(false);

  setSelectedCountryLabel(null);
  setSelectedUserLabel(null);
  setSelectedTriLabel(null);

  onCountryChange(null);
  onUserChange(null);
  onTriChange(null);
  onSearchChange("");
  onResetFilters();
          }}
          className="
            inline-flex
            items-center
            justify-center
            gap-2

            rounded-full

            bg-violet-50
            hover:bg-violet-100

            text-violet-600

            px-5
            py-3

            text-sm
            font-semibold

            transition-colors
            flex-shrink-0
          "
        >
          <i className="las la-redo-alt text-lg" />
          {t('reinitialiser')}
        </button>
      </div>
    </div>
  );
};

export default TabFilters;